import { toast } from 'react-toastify'
import { useMutation } from 'urql'

import ExclamationMarkIcon from '@/assets/icons/ic_logic-g-app_exclamation-mark.svg'
import { graphql } from '@/gql/generated'
import { logout } from '@/urql'
import { getAuthentication } from '@/urql-common'

import { Button } from '../ui/button'
import { Modal, ModalProps } from './Modal'

interface Props extends ModalProps {
  canDelete: boolean
  onRequestClose: () => void
}

export const DeleteUserModal = ({
  isOpen,
  canDelete,
  onRequestClose,
  ...rest
}: Props): JSX.Element => {
  const [_viewerDeleteResult, viewerDeleteMutation] = useMutation(ViewerDeleteMutation)

  const handleDeleteViewer = async () => {
    const authData = getAuthentication()
    if (!authData?.refreshToken) return

    const result = await viewerDeleteMutation({
      input: {
        refreshToken: authData.refreshToken,
      },
    })

    if (result.error || result.data?.viewerDelete?.errors?.length) {
      return toast.error('退会処理に失敗しました', {
        toastId: 'viewer-delete-failed',
        position: 'bottom-right',
      })
    } else {
      await logout('/web/account-stop-completion')
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      showCloseButton={true}
      customStyles={{
        content: {
          textAlign: 'center',
          backgroundColor: '#F5F7FC',
          boxShadow: '0px 2px 2px 0px #00000040',
          width: 'auto',
        },
      }}
      {...rest}
      onRequestClose={onRequestClose}
      className="delete-user-modal"
    >
      {canDelete ? (
        <div className="w-[335px] p-[35px_28px_30px] md:w-[512px] md:p-[40px_31px_30px]">
          <h3 className="mb-[16px] text-lg text-[#222222] md:mb-[28px] md:text-2xl">
            本当に退会しますか？
          </h3>

          <p className="text-xs leading-normal tracking-widest text-[#222222] md:text-[15px]">
            退会すると、学習履歴などお客さま情報が完全に削除されます。よろしいですか？
          </p>
          <div className="mx-auto mt-[16px] flex h-[104px] w-[104px] items-center justify-center rounded-full bg-primary-20 md:mt-[40px]">
            <ExclamationMarkIcon width={82} />
          </div>

          <Button
            variant="secondary"
            className="mx-auto mt-[16px] w-[270px] text-[#222222] md:mt-[40px]"
            onClick={handleDeleteViewer}
          >
            退会する
          </Button>
        </div>
      ) : (
        <div className="w-[335px] p-[35px_28px_30px] md:w-[512px] md:p-[40px_31px_30px]">
          <h3 className="mb-[16px] text-lg text-[#222222] md:mb-[28px] md:text-2xl">
            ご利用期間が残っています
          </h3>

          <p className="text-xs leading-normal tracking-widest text-[#222222] md:text-[15px]">
            ご利用期間が残っているプランがあるため、アカウントの削除ができません。
            <br />
            お支払い設定から、すべてのプランの自動更新を停止してください。
          </p>

          <div className="mx-auto mt-[16px] flex h-[104px] w-[104px] items-center justify-center rounded-full bg-primary-20 md:mt-[40px]">
            <ExclamationMarkIcon width={82} />
          </div>

          <Button
            variant="secondary"
            className="mx-auto mt-[16px] w-[270px] text-[#222222] md:mt-[40px]"
            onClick={onRequestClose}
          >
            閉じる
          </Button>
        </div>
      )}
    </Modal>
  )
}

const ViewerDeleteMutation = graphql(`
  mutation ViewerDeleteMutation($input: ViewerDeleteInput!) {
    viewerDelete(input: $input) {
      errors {
        ... on BaseError {
          field
          message
        }
      }
    }
  }
`)
